import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { contactsApi } from '../../../api/contacts'
import { RefusedError } from '../../../api/client'
import type { ContactAssertion, RefuteReason } from '../../../types/contacts'
import { REASONS, pageOf, provenanceTitle, reasonWords, siteTitles, sourceTitle, sourceWord, statusWords, withdrawable } from './words'

// One fact on the card: its status chip, its value, the sources it stands on, and the controls that move it.
// Confirm opens the 1–5 rating (→ locked in); Not right? opens the reasons (→ refuted, terminal for that
// assertion); Withdraw takes back what the caller themself did. Every write refetches the card.

const RATINGS = [1, 2, 3, 4, 5]

function message(e: unknown): string {
  return e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e)
}

export function FactRow({ fact, spoke, personId, showSite, onChanged }: {
  fact: ContactAssertion; spoke: string; personId: string
  /** in the "your call" strip: name the site the fact sits on */
  showSite?: boolean
  onChanged: () => void
}) {
  const [mode, setMode] = useState<'idle' | 'rate' | 'refute'>('idle')
  const [reason, setReason] = useState<RefuteReason | null>(null)
  const [note, setNote] = useState('')
  const settle = () => { setMode('idle'); setReason(null); setNote(''); onChanged() }
  const lock = useMutation({ mutationFn: (rating: number) => contactsApi.lockFact(spoke, personId, fact.assertionId, rating), onSuccess: settle })
  const refute = useMutation({ mutationFn: (r: RefuteReason) => contactsApi.refuteFact(spoke, personId, fact.assertionId, r, note.trim() || undefined), onSuccess: settle })
  const withdraw = useMutation({ mutationFn: () => contactsApi.withdrawFact(spoke, personId, fact.assertionId), onSuccess: settle })
  const busy = lock.isPending || refute.isPending || withdraw.isPending
  const failed = lock.error ?? refute.error ?? withdraw.error
  const refuted = fact.status === 'refuted'
  // "this record is also that record" (PL-209): Confirm combines the two into this one; Not right says they are two people
  const samePerson = fact.key === 'same-person'

  return (
    <li className={`fact-row status-${fact.status}`} data-assertion={fact.assertionId}>
      <div className="fact-main">
        <div className="fact-line">
          {showSite && <span className="tag site-tag">{siteTitles[fact.site]}</span>}
          <span className={`tag status status-${fact.status}`} title={provenanceTitle(fact)} aria-label={fact.status === 'locked' && fact.rating ? `locked in, rated ${fact.rating} of 5` : undefined}>{statusWords(fact)}</span>
          <span className={`fact-value${refuted ? ' struck' : ''}`}>{fact.value}</span>
          {fact.sensitivity === 'personal' && <span className="tag mine-only" title="Told to you alone: never on the graph, never in a draft unless you put it there">mine only</span>}
        </div>
        {fact.sources.length > 0 && (
          <p className="muted small fact-sources">
            {fact.sources.map((s, i) => (
              // a document source carries the words it stands on as the tooltip (REQ-120)
              <span key={`${s.kind}-${s.reference ?? ''}-${i}`}>{i > 0 ? ' · ' : ''}{pageOf(s.reference)
                ? <a className={`fact-source kind-${s.kind}`} href={pageOf(s.reference)!.url} target="_blank" rel="noreferrer" title={sourceTitle(s)}>{sourceWord(s)}</a>
                : <span className={`fact-source kind-${s.kind}`} title={sourceTitle(s)}>{sourceWord(s)}</span>}</span>
            ))}
          </p>
        )}
      </div>
      <div className="fact-controls">
        {fact.status !== 'locked' && (
          <button className={`button small${mode === 'rate' ? ' on' : ''}`} onClick={() => setMode(mode === 'rate' ? 'idle' : 'rate')} disabled={busy}
            title={samePerson ? 'These are one person: the other record folds into this one, every address kept, and no re-run undoes it.' : 'You know this is right: rate how useful it is and it is locked in.'}>{samePerson ? 'Combine' : 'Confirm'}</button>
        )}
        {!refuted && (
          <button className={`button small${mode === 'refute' ? ' on' : ''}`} onClick={() => setMode(mode === 'refute' ? 'idle' : 'refute')} disabled={busy}
            title={samePerson ? 'Two different people: Scout never proposes them again.' : 'Say why it is wrong; the next re-run obeys.'}>{samePerson ? 'Not the same person' : 'Not right?'}</button>
        )}
        {withdrawable(fact) && (
          <button className="button small" onClick={() => withdraw.mutate()} disabled={busy} title="Take back what you said, locked or marked not right.">{withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}</button>
        )}
      </div>
      {mode === 'rate' && (
        <div className="fact-picker" role="group" aria-label="Rate how useful this is">
          <span className="muted small">How useful is this, 1 to 5?</span>
          {RATINGS.map((n) => (
            <button key={n} className="button small rate" aria-label={`Rate ${n}`} onClick={() => lock.mutate(n)} disabled={busy}>{n}</button>
          ))}
        </div>
      )}
      {mode === 'refute' && (
        <div className="fact-picker" role="group" aria-label="Why it is not right">
          <span className="muted small">Why?</span>
          {REASONS.map((r) => (
            <button key={r} className={`button small reason${reason === r ? ' on' : ''}`} aria-pressed={reason === r} onClick={() => setReason(r)} disabled={busy}>{reasonWords[r]}</button>
          ))}
          <input className="fact-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note (optional)" aria-label="A note (optional)" disabled={busy} />
          <button className="button small primary" onClick={() => reason && refute.mutate(reason)} disabled={!reason || busy}>{refute.isPending ? 'Saving…' : 'Mark not right'}</button>
        </div>
      )}
      {failed && <p className="error small">{message(failed)}</p>}
    </li>
  )
}
